import { Group, Stack, Text } from "@mantine/core"
import { MultiSelect, ProgressBox, Select } from "@tfg/ui"
import { Trans, useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { FindAgecComplianceStatsDto } from "~/gen/api"
import { IngestionDateFilter } from "~/pages/Brand/Home/hooks/useIngestionDateFilter"

export type AgecComplianceContentProps = {
  collectionFilter: string[]
  setCollectionFilter: (value: string[]) => void
  brandTypeFilter: string[]
  setBrandTypeFilter: (value: string[]) => void
  collectionOptions: { label: string; value: string }[]
  brandTypeOptions: { label: string; value: string }[]
  ingestionDateOptions: { label: string; value: string }[]
  ingestionDateFilter: IngestionDateFilter | null
  setIngestionDateFilter: (value: IngestionDateFilter | null) => void
  stats: FindAgecComplianceStatsDto
}

export const AgecComplianceContent = ({
  collectionFilter,
  setCollectionFilter,
  brandTypeFilter,
  setBrandTypeFilter,
  collectionOptions,
  brandTypeOptions,
  stats,
  ingestionDateOptions,
  ingestionDateFilter,
  setIngestionDateFilter,
}: AgecComplianceContentProps) => {
  const { t } = useTranslation(["home", "common"])

  return (
    <Stack>
      <Group wrap="nowrap">
        <MultiSelect
          clearable
          data={collectionOptions}
          value={collectionFilter}
          onChange={setCollectionFilter}
          placeholder={t("informationRequests.collection")}
          size="xs"
        />
        <MultiSelect
          clearable
          data={brandTypeOptions}
          value={brandTypeFilter}
          onChange={setBrandTypeFilter}
          placeholder={t("informationRequests.type")}
          size="xs"
        />
        <Select
          clearable
          data={ingestionDateOptions}
          value={ingestionDateFilter}
          onChange={(value) => {
            setIngestionDateFilter(value as IngestionDateFilter)
          }}
          placeholder={t("informationRequests.ingestionDate")}
          size="xs"
        />
      </Group>
      {stats.total > 0 ? (
        <Stack bg="white" px="xs" py="md">
          <ProgressBox
            label={t("agecCompliance.label")}
            sections={[
              {
                color: "var(--progress-bar-color-filled)",
                textColor: "var(--progress-bar-filled-text)",
                value: stats.completedPercent,
                text: stats.completedCount.toString(),
                label: t("agecCompliance.compliant"),
                popover: (
                  <>
                    <Text>
                      <Trans
                        ns="home"
                        i18nKey="agecCompliance.compliant"
                        values={{
                          count: stats.completedCount,
                        }}
                        components={{
                          link1: (
                            <Link
                              to="/regulations/agec?agecCompliance=yes"
                              style={{
                                textDecoration: "underline",
                                color: "var(--mantine-color-blue-6)",
                              }}
                            />
                          ),
                        }}
                      >
                        <Link to="/regulations/agec?agecCompliance=yes">
                          {t("agecCompliance.compliant")}
                        </Link>
                      </Trans>
                    </Text>
                  </>
                ),
              },
              {
                color: "var(--progress-bar-color-unfilled)",
                textColor: "var(--progress-bar-unfilled-text)",
                value: stats.notCompletedPercent,
                text: stats.notCompletedCount.toString(),
                label: t("agecCompliance.nonCompliant"),
                popover: (
                  <>
                    <Text>
                      <Trans
                        ns="home"
                        i18nKey="agecCompliance.nonCompliant"
                        values={{
                          count: stats.notCompletedCount,
                        }}
                        components={{
                          link1: (
                            <Link
                              to="/regulations/agec?agecCompliance=no"
                              style={{
                                textDecoration: "underline",
                                color: "var(--mantine-color-blue-6)",
                              }}
                            />
                          ),
                        }}
                      />
                    </Text>
                  </>
                ),
              },
            ]}
            helpHoverText={<Text>{t("agecCompliance.totalText")}</Text>}
          />
          <Stack gap="0">
            <Text fz="sm">
              <Trans
                ns="home"
                i18nKey="agecCompliance.total"
                values={{
                  total: stats.total,
                }}
                components={{
                  strong1: (
                    <strong
                      style={{
                        color: "var(--progress-bar-color-filled)",
                      }}
                    />
                  ),
                }}
              />
            </Text>
            <Text fz="sm">
              <Trans
                ns="home"
                i18nKey="agecCompliance.complete"
                values={{
                  percent: stats.completedPercent,
                }}
                components={{
                  strong1: (
                    <strong
                      style={{
                        color: "var(--progress-bar-color-filled)",
                      }}
                    />
                  ),
                }}
              />
            </Text>
          </Stack>
        </Stack>
      ) : (
        <Text fz="sm">
          <strong>{t("informationRequests.noProducts")}</strong>
        </Text>
      )}
    </Stack>
  )
}
